import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isValidEmail } from '../utilities/emailValidation'

export default function useLocationValidation({ verifiedRef } = {}) {
  const { t } = useTranslation()
  const [errors, setErrors] = useState({})
  const internalVerifiedRef = useRef(new Set())
  const verifiedAddressesRef = verifiedRef || internalVerifiedRef

  const normalizePhone = (phone) => (phone || '').replace(/\D/g, '')

  const isValidPhone = (phone) => {
    const digits = normalizePhone(phone)
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))
  }

  const getValidationErrors = (location) => {
    const nextErrors = {}
    if (!location.name?.trim()) nextErrors.name = t('Required')
    if (!location.address?.trim()) nextErrors.address = t('Required')
    if (!location.contact_name?.trim()) nextErrors.contact_name = t('Required')
    if (!location.contact_phone?.trim()) nextErrors.contact_phone = t('Required')
    else if (!isValidPhone(location.contact_phone)) nextErrors.contact_phone = t('Enter a valid phone')
    if (!location.contact_email?.trim()) nextErrors.contact_email = t('Required')
    else if (!isValidEmail(location.contact_email)) nextErrors.contact_email = t('Enter a valid email')
    return nextErrors
  }

  const isLocationComplete = (location) => Object.keys(getValidationErrors(location)).length === 0

  const validate = (location) => {
    const nextErrors = getValidationErrors(location)
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const clearErrors = () => setErrors({})

  const addressCacheKey = (location, quoteLocation) =>
    [location.address, quoteLocation?.city, quoteLocation?.state, quoteLocation?.postal_code]
      .map((part) => (part || '').trim().toLowerCase())
      .join('|')

  const markVerified = (location, quoteLocation) => {
    verifiedAddressesRef.current.add(addressCacheKey(location, quoteLocation))
  }

  const validateWithAddress = async (location, quoteLocation, validateAddressFn) => {
    const nextErrors = getValidationErrors(location)
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return { ok: false, reason: 'invalid_fields' }
    }

    setErrors({})
    const cacheKey = addressCacheKey(location, quoteLocation)
    if (verifiedAddressesRef.current.has(cacheKey)) return { ok: true }

    const result = await validateAddressFn({
      address: location.address,
      city: quoteLocation?.city,
      state: quoteLocation?.state,
      postal_code: quoteLocation?.postal_code,
    })

    if (result.valid && !result.normalized) {
      verifiedAddressesRef.current.add(cacheKey)
      return { ok: true }
    }

    const suggestion = result.normalized
      ? { ...result.normalized, formattedAddress: result.formattedAddress }
      : null

    const enteredMatchesSuggestion =
      suggestion &&
      suggestion.address?.trim().toLowerCase() === (location.address || '').trim().toLowerCase()

    if (result.valid && enteredMatchesSuggestion) {
      verifiedAddressesRef.current.add(cacheKey)
      return { ok: true }
    }

    return { ok: false, reason: 'unverified', suggestion }
  }

  return { errors, validate, validateWithAddress, markVerified, clearErrors, isLocationComplete }
}
